import { captureException } from '@sentry/react'
import { generateAiImg_API } from 'api'
import { PROJECT_MEDIA_TYPES, SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { ErrorKeys } from 'consts/errorKeys'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { withAppThunks } from 'store/with-app-thunks'
import { TAppDispatch, TGetState } from 'store/types'
import { MediaStatus } from 'types/media'
import { getDeviceVersion, scrollToElementById } from 'utils'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import {
  prepareGenerationPayload,
  createNewMediaItem
} from 'store/features/aiImage/utils'
import aiImageSlice from 'store/features/aiImage/slice'
import { handleApiError } from 'store/utils'
import { generationProgressService } from 'services/generationProgress'
const { actions } = aiImageSlice

const EXPECTED_DURATION_SECONDS = 10

const checkUserCredits = () =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const state = getState()
      await dispatch(appThunks.creditsThunks.getUserCredits())
      const updatedHasEnoughCredits = aiImageSelectors.hasEnoughCredits(state)

      if (!updatedHasEnoughCredits) {
        //TODOT waiting for upgrade popup case
        throw new Error(
          'You do not have enough credits to generate this content'
        )
      }
    }
  )

const _handleApiError = (error: unknown) => (dispatch: TAppDispatch) => {
  if (!(error instanceof ApiError)) {
    captureException(new ReduxThunkError(error))
  }

  if (
    (error as Error & { cause?: { code?: string } })?.cause?.code ===
    'ModerationError'
  ) {
    return dispatch(handleApiError(ErrorKeys.AI_MODERATION_FAILED))
  }

  dispatch(handleApiError(ErrorKeys.AI_VIDEO_GENERATION_FAILED))
}

const generateImage = (insertionStartTime: number = 0) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const state = getState()
      const isMobile = getDeviceVersion(SMALL_DESKTOP_SIZE_BREAKPOINT)
      const generateForm = aiImageSelectors.generateForm(state)
      const defaultDimensions = projectDataSelectors.defaultDimensions(state)
      const aspectRatio = generateForm.aspectRatio
      try {
        const projectId = await dispatch(
          appThunks.projectThunks.getOrCreateProjectId()
        )
        // Prepare the generation payload
        const preparedForm = prepareGenerationPayload(generateForm, projectId)
        dispatch(actions.setIsRequestingGeneration(true))
        if (isMobile) {
          dispatch(appThunks.editorThunks.setIsMobileSubSidebarOpen(false))
        }

        // Check if user has enough credits before generation
        await dispatch(checkUserCredits())

        // Generate new AI image
        const response = await generateAiImg_API(preparedForm)
        const { createdAiImageProjectIds, projectId: imageProjectId } =
          response.data

        if (!createdAiImageProjectIds) {
          return
        }

        // Create new media item for timeline
        const assetId = createdAiImageProjectIds[0]
        const preparedItem = createNewMediaItem(
          imageProjectId,
          assetId,
          insertionStartTime,
          defaultDimensions,
          aspectRatio!
        )

        generationProgressService.start(
          preparedItem.id,
          preparedItem.assetId,
          EXPECTED_DURATION_SECONDS,
          false,
          0
        )

        dispatch(
          appThunks.projectDataThunks.addMediaItems({ value: [preparedItem] })
        )
        dispatch(
          appThunks.editorThunks.setActiveTimelineItems([
            {
              id: preparedItem.id,
              type: preparedItem.type
            }
          ])
        )

        scrollToElementById(preparedItem.id)
      } catch (error: unknown) {
        dispatch(_handleApiError(error))
      } finally {
        dispatch(actions.setIsRequestingGeneration(false))
      }
    }
  )

const regenerateImage = () =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const state = getState()
      const isMobile = getDeviceVersion(SMALL_DESKTOP_SIZE_BREAKPOINT)
      const generateForm = aiImageSelectors.generateForm(state)
      const mediaItems = projectDataSelectors.mediaItems(state)
      const targetMediaItem = mediaItems.find(
        item =>
          item.id === generateForm.targetMediaItemId &&
          item.type === PROJECT_MEDIA_TYPES.aiImage
      )
      try {
        const projectId = await dispatch(
          appThunks.projectThunks.getOrCreateProjectId()
        )

        // Prepare the generation payload (same as generateImage)
        const preparedForm = prepareGenerationPayload(generateForm, projectId)

        dispatch(actions.setIsRequestingGeneration(true))
        if (isMobile) {
          dispatch(appThunks.editorThunks.setIsMobileSubSidebarOpen(false))
        }

        // Check if user has enough credits before generation
        await dispatch(checkUserCredits())

        // Generate new AI image
        const response = await generateAiImg_API(preparedForm)
        const { createdAiImageProjectIds, projectId: imageProjectId } =
          response.data

        if (!createdAiImageProjectIds || !targetMediaItem) {
          return
        }

        const assetId = createdAiImageProjectIds[0].toString()

        generationProgressService.start(
          targetMediaItem.id,
          assetId,
          EXPECTED_DURATION_SECONDS,
          false,
          0
        )

        // Update existing media item instead of creating new one
        dispatch(
          appThunks.projectDataThunks.updateMediaAfterGeneration({
            id: targetMediaItem.id,
            updates: {
              assetId: createdAiImageProjectIds[0].toString(),
              meta: {
                ...targetMediaItem.meta,
                assetParentId: imageProjectId.toString(),
                status: MediaStatus.PENDING
              }
            }
          })
        )
        dispatch(
          appThunks.editorThunks.selectTimelineItem(
            targetMediaItem.id,
            targetMediaItem.type
          )
        )
      } catch (error: unknown) {
        dispatch(_handleApiError(error))
      } finally {
        dispatch(actions.setIsRequestingGeneration(false))
      }
    }
  )

export const generateThunks = {
  generateImage,
  regenerateImage
}
